import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { getPublishedProductBySlug } from '@/services/products';
import { getUserLicensesForProduct, connectLicenseToSite } from '@/services/licenses';
import type { Product, License } from '@/domain/types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtExpiry(iso: string | null) {
  if (!iso) return 'Lifetime';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function statusColor(status: string) {
  if (status === 'active') return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
  if (status === 'inactive') return 'bg-surface-100 text-surface-600 ring-1 ring-surface-200';
  if (status === 'grace') return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
  return 'bg-surface-100 text-surface-500';
}

// ── Inline sign-in form ───────────────────────────────────────────────────────

function SignInPrompt({ onSuccess }: { onSuccess: () => void }) {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email.trim().toLowerCase(), password);
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-surface-200 shadow-sm p-6 md:p-8 max-w-sm w-full mx-auto">
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-2xl bg-brand-50 flex items-center justify-center mx-auto mb-3">
          <i className="ri-user-line text-xl text-brand-600" />
        </div>
        <h3 className="font-bold text-surface-900 text-base">Sign in to continue</h3>
        <p className="text-sm text-surface-500 mt-1">You need to be signed in to connect a license.</p>
      </div>
      <form onSubmit={handleSignIn} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
            placeholder="you@example.com"
            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            placeholder="••••••••"
            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" />
        </div>
        {error && (
          <p className="text-xs text-red-600 flex items-center gap-1"><i className="ri-error-warning-line" /> {error}</p>
        )}
        <button type="submit" disabled={loading}
          className="w-full py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2">
          {loading && <i className="ri-loader-4-line animate-spin" />}
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
      <p className="text-center text-xs text-surface-400 mt-4">
        Don't have an account? <Link to="/register" className="text-brand-600 hover:underline">Register</Link>
      </p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ConnectPage() {
  const [searchParams]  = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const productSlug = searchParams.get('product_slug') ?? '';
  const domain      = searchParams.get('domain') ?? '';
  const callbackUrl = searchParams.get('callback_url') ?? '';
  const state       = searchParams.get('state') ?? '';

  const [product, setProduct]               = useState<Product | null>(null);
  const [licenses, setLicenses]             = useState<License[]>([]);
  const [selectedLicenseId, setSelected]    = useState('');
  const [loading, setLoading]               = useState(true);
  const [connecting, setConnecting]         = useState(false);
  const [error, setError]                   = useState('');
  const [connected, setConnected]           = useState(false);

  const isValidRequest = Boolean(productSlug && domain && callbackUrl && state);

  const load = useCallback(async () => {
    if (!user || !isValidRequest) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const prod = await getPublishedProductBySlug(productSlug);
      if (!prod) { setError('Product not found.'); setLoading(false); return; }
      setProduct(prod);

      const userLicenses = await getUserLicensesForProduct(prod.id);
      setLicenses(userLicenses);

      // Auto-select best license: active with available slots → inactive → first
      const best =
        userLicenses.find((l) => l.status === 'active' && l.activatedSites < (l.siteLimit === 999 ? Infinity : l.siteLimit)) ||
        userLicenses.find((l) => l.status === 'inactive') ||
        userLicenses.find((l) => l.status === 'grace') ||
        userLicenses[0];
      if (best) setSelected(best.id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load licenses.');
    } finally {
      setLoading(false);
    }
  }, [isValidRequest, productSlug, user]);

  useEffect(() => {
    if (!authLoading) load();
  }, [authLoading, load]);

  const handleConnect = async () => {
    if (!selectedLicenseId || !domain) return;
    setError('');
    setConnecting(true);
    try {
      const authCode = await connectLicenseToSite(selectedLicenseId, domain, state, productSlug, callbackUrl);
      setConnected(true);

      // Redirect back to plugin with auth_code + state
      // Plugin will call connect-exchange edge function to get the license_key
      const redirect = new URL(decodeURIComponent(callbackUrl));
      redirect.searchParams.set('auth_code', authCode);
      redirect.searchParams.set('state', state);

      setTimeout(() => {
        window.location.href = redirect.toString();
      }, 1200);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Connection failed. Please try again.');
      setConnecting(false);
    }
  };

  // ── Invalid request ────────────────────────────────────────────────────────
  if (!isValidRequest) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-surface-900 mb-2">Invalid Request</h2>
          <p className="text-sm text-surface-500">This connection link is missing required parameters. Please try activating from your WordPress plugin again.</p>
        </div>
      </div>
    );
  }

  const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-50 to-brand-50/20 flex flex-col items-center justify-center p-4 py-12">

      {/* Brand header */}
      <div className="mb-8 text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-xl bg-brand-600 flex items-center justify-center">
            <i className="ri-store-2-line text-white text-sm" />
          </div>
          <span className="font-bold text-surface-900 text-base">Motionsdio Store</span>
        </Link>
        <p className="text-xs text-surface-400">Secure License Connection</p>
      </div>

      {/* Not signed in → inline sign-in */}
      {!authLoading && !user ? (
        <div className="w-full max-w-sm">
          {/* Context card above sign-in */}
          <div className="bg-white rounded-2xl border border-surface-200 p-4 mb-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
              <i className="ri-link text-brand-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-surface-500">Connecting</p>
              <p className="text-sm font-semibold text-surface-900 truncate">{cleanDomain}</p>
            </div>
            <i className="ri-arrow-right-line text-surface-300 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-surface-500">Plugin</p>
              <p className="text-sm font-semibold text-surface-900 truncate capitalize">{productSlug.replace(/-/g, ' ')}</p>
            </div>
          </div>
          <SignInPrompt onSuccess={load} />
        </div>
      ) : (
        <div className="w-full max-w-lg">

          {/* Connection card */}
          <div className="bg-white rounded-2xl border border-surface-200 shadow-sm overflow-hidden">

            {/* Header */}
            <div className="px-6 py-5 border-b border-surface-100">
              <div className="flex items-center gap-4">
                {/* Site icon */}
                <div className="w-12 h-12 rounded-xl bg-surface-100 border border-surface-200 flex items-center justify-center shrink-0">
                  <i className="ri-global-line text-xl text-surface-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-surface-900 truncate">{cleanDomain}</p>
                  <p className="text-xs text-surface-400 mt-0.5">wants to connect to your license</p>
                </div>
                <i className="ri-link text-surface-300 text-xl shrink-0 mx-1" />
                {product?.thumbnail ? (
                  <img src={product.thumbnail} alt={product.name} className="w-12 h-12 rounded-xl object-cover border border-surface-200 shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-brand-50 border border-brand-100 flex items-center justify-center shrink-0">
                    <i className="ri-plug-line text-brand-500 text-xl" />
                  </div>
                )}
              </div>

              {product && (
                <div className="mt-4 px-4 py-3 rounded-xl bg-surface-50 border border-surface-100">
                  <p className="text-xs text-surface-500 mb-0.5">Plugin</p>
                  <p className="text-sm font-semibold text-surface-900">{product.name}</p>
                  {product.shortDescription && (
                    <p className="text-xs text-surface-400 mt-0.5 line-clamp-1">{product.shortDescription}</p>
                  )}
                </div>
              )}
            </div>

            {/* Body */}
            <div className="px-6 py-5">

              {loading ? (
                <div className="py-8 flex flex-col items-center gap-3">
                  <i className="ri-loader-4-line animate-spin text-2xl text-surface-300" />
                  <p className="text-sm text-surface-400">Loading your licenses…</p>
                </div>
              ) : connected ? (
                <div className="py-6 flex flex-col items-center gap-3 text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                    <i className="ri-check-line text-3xl text-emerald-500" />
                  </div>
                  <p className="font-bold text-surface-900">Connected!</p>
                  <p className="text-sm text-surface-500">Redirecting you back to {cleanDomain}…</p>
                </div>
              ) : licenses.length === 0 ? (
                <div className="py-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-key-2-line text-xl text-amber-500" />
                  </div>
                  <p className="font-semibold text-surface-900 mb-1">No licenses found</p>
                  <p className="text-sm text-surface-500 mb-4">
                    You don't have a license for <strong>{product?.name ?? productSlug}</strong>.
                    Purchase one to connect this plugin.
                  </p>
                  <Link
                    to={`/product/${productSlug}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors"
                  >
                    <i className="ri-shopping-cart-line" /> Buy a License
                  </Link>
                </div>
              ) : (
                <>
                  <p className="text-sm font-semibold text-surface-900 mb-3">Choose a license to connect</p>
                  <div className="space-y-2 mb-5">
                    {licenses.map((lic) => {
                      const slots     = lic.siteLimit === 999 ? '∞' : lic.siteLimit;
                      const used      = lic.activatedSites;
                      const full      = lic.siteLimit !== 999 && used >= lic.siteLimit;
                      const isSelected = selectedLicenseId === lic.id;
                      return (
                        <label
                          key={lic.id}
                          className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                            full ? 'opacity-50 cursor-not-allowed border-surface-200 bg-surface-50' :
                            isSelected ? 'border-brand-500 bg-brand-50/50 shadow-sm' :
                            'border-surface-200 hover:border-brand-300 bg-white'
                          }`}
                        >
                          <input
                            type="radio"
                            name="license"
                            value={lic.id}
                            checked={isSelected}
                            disabled={full}
                            onChange={() => setSelected(lic.id)}
                            className="w-4 h-4 accent-brand-600 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-semibold text-surface-900">{lic.planName || 'License'}</span>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full capitalize ${statusColor(lic.status)}`}>
                                {lic.status}
                              </span>
                              {full && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 ring-1 ring-red-200">Full</span>}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-surface-400">
                              <span className="font-mono">****{lic.licenseKeyLast4}</span>
                              <span>{used}/{slots} sites used</span>
                              <span>Expires: {fmtExpiry(lic.expiresAt)}</span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {error && (
                    <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm mb-4">
                      <i className="ri-error-warning-line shrink-0 mt-0.5" /> {error}
                    </div>
                  )}

                  {/* What this grants */}
                  <div className="p-3.5 rounded-xl bg-surface-50 border border-surface-100 mb-5">
                    <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-2">This will</p>
                    <ul className="space-y-1.5">
                      {[
                        `Activate your license on ${cleanDomain}`,
                        'Allow the plugin to receive automatic updates',
                        'Count as 1 site slot on your selected license',
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2 text-xs text-surface-600">
                          <i className="ri-check-line text-brand-500 shrink-0 mt-0.5" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={handleConnect}
                    disabled={!selectedLicenseId || connecting}
                    className="w-full py-3 rounded-xl bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 shadow-sm"
                  >
                    {connecting && <i className="ri-loader-4-line animate-spin" />}
                    {connecting ? 'Connecting…' : `Connect ${cleanDomain}`}
                  </button>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-surface-100 bg-surface-50/50 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs text-surface-400">
                <i className="ri-shield-check-line text-emerald-500" />
                Secure · Your license key is never shared with third parties
              </div>
              <Link to="/" className="text-xs text-surface-400 hover:text-brand-600 transition-colors">Cancel</Link>
            </div>
          </div>

          {/* Signed in as */}
          {user && (
            <p className="text-center text-xs text-surface-400 mt-4">
              Signed in as <span className="font-medium text-surface-600">{user.email}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
